import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, Grid, Box, Button, Pagination, Tabs, Tab, Chip, useMediaQuery, Theme, TextField, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import { getMovieByStatus, getMovieByStatusForUser, getProfileMovieStatuses, getPublicMovieStatuses } from '../../services/movieApi';
import MovieCard from '../MovieCard';
import { UserProfile } from '../../types/UserProfile';
import { Movie } from '../../types/Movie';
import LoadingPlaceholder from '../LoadingPlaceholder';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

const MOVIES_PER_PAGE = 30;

interface MovieLibraryTabProps {
  profile: UserProfile | null;
  isPublic: boolean;
  movieCounts: number[];
  mainTabValue: string;
}

const MovieLibraryTab: React.FC<MovieLibraryTabProps> = ({
  profile,
  isPublic,
  movieCounts,
  mainTabValue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initialStatus = parseInt(queryParams.get('status') || '1', 10);
  const initialPage = parseInt(queryParams.get('page') || '1', 10);
  const initialSearchQuery = queryParams.get('query') || '';
  const initialOrder = queryParams.get('order') || 'default';

  const [tabValue, setTabValue] = useState(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [displayQuery, setDisplayQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);

  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const updateUrl = useCallback((status: number, pg: number, query: string, ord: string) => {
    const params = new URLSearchParams({
      status: status.toString(),
      page: pg.toString(),
      query,
      order: ord,
      tab: mainTabValue,
      media: 'movie',
    });

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [isPublic, username, mainTabValue, navigate]);

  const stableUsername = isPublic ? username : 'me';
  const {
    data: movieData,
    isLoading,
  } = useQuery({
    queryKey: ['profileMovie', stableUsername, tabValue, page, searchQuery, order],
    queryFn: async () => {
      if (isPublic) {
        return getMovieByStatusForUser(username!, tabValue, page, searchQuery, order);
      } else {
        return getMovieByStatus(tabValue, page, searchQuery, order);
      }
    },
    enabled: mainTabValue === 'library',
    staleTime: isPublic ? 30000 : 2000,
  });

  const movieList: Movie[] = movieData?.data.results || [];
  const totalMovies = movieData?.data.count || 0;
  const movieTotalPages = Math.ceil(totalMovies / MOVIES_PER_PAGE);
  const loadingMovies = isLoading && !movieData;

  const movieIds = useMemo(() => movieList.map(m => m.movie_id), [movieList]);

  const { data: movieStatusesData } = useQuery({
    queryKey: ['profileMovieStatuses', stableUsername, movieIds],
    queryFn: async () => {
      if (isPublic) {
        return getPublicMovieStatuses(username!, movieIds);
      } else {
        return getProfileMovieStatuses(movieIds);
      }
    },
    enabled: movieIds.length > 0,
    staleTime: isPublic ? 30000 : 2000,
  });

  const movieStatuses = movieStatusesData || {};

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
      setPage(1);
      updateUrl(tabValue, 1, query, order);
    }, 1000),
    [tabValue, order, updateUrl]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setDisplayQuery(query);
    debouncedSearch(query);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setOrder(newOrder);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, newOrder);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentScroll = scrollPositionRef.current;
    setTabValue(newValue);
    setPage(1);
    updateUrl(newValue, 1, searchQuery, order);
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrl(tabValue, value, searchQuery, order);
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setDisplayQuery('');
    setOrder('default');
    setPage(1);
    updateUrl(tabValue, 1, '', 'default');
  };

  useEffect(() => {
    const newStatus = parseInt(queryParams.get('status') || '1', 10);
    if (newStatus !== tabValue) {
      setTabValue(newStatus);
      setPage(1);
    }
  }, [location.search]);

  const isFilterActive = displayQuery !== '' || order !== 'default';

  const TabLabel: React.FC<{ label: string; count: number; isSelected: boolean }> = ({ label, count, isSelected }) => (
    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: 'center' }}>
      <Typography sx={{ mr: isSmallScreen ? 0 : 1, fontSize: isSmallScreen ? '0.75rem' : '0.8rem' }}>{label}</Typography>
      {isSelected ? (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'black',
            fontSize: isSmallScreen ? '0.625rem' : '0.75rem',
            height: isSmallScreen ? '16px' : '18px',
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.625rem' : '0.75rem' }}>
          /{count}
        </Typography>
      )}
    </Box>
  );

  const orderDisplayText: { [key: string]: string } = {
    'default': 'デフォルト',
    'rating': '評価 (低い順)',
    'rating_desc': '評価 (高い順)',
    'start_date': '視聴日 (古い順)',
    'start_date_desc': '視聴日 (新しい順)',
    'added_date': '追加日 (古い順)',
    'added_date_desc': '追加日 (新しい順)',
    'year_desc': '公開年 (新しい順)',
    'year_asc': '公開年 (古い順)',
  };

  const orderDisplayTextShort: { [key: string]: string } = {
    'default': 'Sort',
    'rating': '評価↑',
    'rating_desc': '評価↓',
    'start_date': '視聴日↑',
    'start_date_desc': '視聴日↓',
    'added_date': '追加日↑',
    'added_date_desc': '追加日↓',
    'year_desc': '公開年↓',
    'year_asc': '公開年↑',
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            maxWidth: '100%',
            '& .MuiTab-root': {
              minWidth: isSmallScreen ? 'auto' : 'inherit',
              padding: isSmallScreen ? '6px 8px' : '6px 16px',
              minHeight: '40px',
              borderRadius: '8px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }
          }}
        >
          <Tab label={<TabLabel label="視聴済" count={movieCounts[0]} isSelected={tabValue === 1} />} value={1} />
          <Tab label={<TabLabel label="視聴中止" count={movieCounts[3]} isSelected={tabValue === 4} />} value={4} />
          <Tab label={<TabLabel label={isSmallScreen ? "ウォッチ" : "ウォッチリスト"} count={movieCounts[4]} isSelected={tabValue === 5} />} value={5} />
        </Tabs>
      </Box>

      {/* フィルター */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            placeholder="Filter..."
            variant="standard"
            size="small"
            value={displayQuery}
            onChange={handleSearchChange}
            sx={{
              flexGrow: 1,
              minWidth: 60,
              '& .MuiInputBase-input::placeholder': {
                opacity: 0.5
              }
            }}
          />
          <Select
            value={order}
            onChange={handleOrderChange}
            displayEmpty
            size="small"
            sx={{
              minWidth: 80,
              height: { xs: '28px', sm: '32px' },
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '4px',
              '& .MuiSelect-select': {
                paddingY: 0,
                paddingX: 1,
                paddingRight: { xs: '0px !important', sm: '32px !important' },
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
              '& .MuiSelect-icon': {
                display: { xs: 'none', sm: 'block' }
              }
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <SortIcon sx={{ mr: 0.5, fontSize: { xs: '0.8rem', sm: '1rem' }, flexShrink: 0 }} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                  {orderDisplayText[selected] || '並び替え'}
                </Box>
                <Box component="span" sx={{ display: { xs: 'block', sm: 'none' } }}>
                  {orderDisplayTextShort[selected] || '並び替え'}
                </Box>
              </Box>
            )}
          >
            <MenuItem value="default" dense>デフォルト</MenuItem>
            <MenuItem value="rating_desc" dense>評価 (高い順)</MenuItem>
            <MenuItem value="rating" dense>評価 (低い順)</MenuItem>
            <MenuItem value="start_date_desc" dense>視聴日 (新しい順)</MenuItem>
            <MenuItem value="start_date" dense>視聴日 (古い順)</MenuItem>
            <MenuItem value="added_date_desc" dense>追加日 (新しい順)</MenuItem>
            <MenuItem value="added_date" dense>追加日 (古い順)</MenuItem>
            <MenuItem value="year_desc" dense>公開年 (新しい順)</MenuItem>
            <MenuItem value="year_asc" dense>公開年 (古い順)</MenuItem>
          </Select>
        </Box>
        {isFilterActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalMovies}件
            </Typography>
            <Button
              size="small"
              onClick={handleClearFilter}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ textTransform: 'none', p: '2px 8px' }}
            >
              Clear
            </Button>
          </Box>
        )}
      </Box>

      {loadingMovies ? (
        <LoadingPlaceholder type="card" count={MOVIES_PER_PAGE} />
      ) : (
        <>
          {movieList.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mt: 2
            }}>
              <Typography variant="h6" gutterBottom>
                まだ映画が登録されていません
              </Typography>
              {!isPublic && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    映画を検索して、ライブラリに追加しましょう
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/search?mode=movie')}
                  >
                    映画を探す
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {movieList.map((movie) => (
                <Grid item xs={4} sm={3} md={2} key={movie.movie_id}>
                  <MovieCard
                    movie={movie}
                    mode={isPublic ? 'public' : 'library'}
                    initialStatus={movieStatuses[movie.movie_id]?.status}
                    initialScore={movieStatuses[movie.movie_id]?.score}
                    initialSubStatus={movieStatuses[movie.movie_id]?.sub_status}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {movieTotalPages > 1 && (
            <Pagination
              count={movieTotalPages}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}
    </>
  );
};

export default MovieLibraryTab;
